import { FC } from "react";

import { Image, Text } from "theme-ui";

import { Page } from "src/components/layout";
import { Permission } from "src/components/permission";
import { PermissionProvider } from "src/contexts/permission-context";
import { ResourcePermissionGrant, useSyncTemplatesQuery } from "src/graphql";
import { Row } from "src/ui/box";
import { Button } from "src/ui/button";
import { Heading } from "src/ui/heading";
import { ChevronDownIcon } from "src/ui/icons";
import { PageSpinner } from "src/ui/loading";
import { Menu } from "src/ui/menu";
import { Table } from "src/ui/table";
import { LastUpdatedColumn } from "src/ui/table/columns/last-updated";
import { useDestinations } from "src/utils/destinations";
import { useNavigate } from "src/utils/navigate";
import { SourceBadges, SourceIcon, useSources } from "src/utils/sources";
import { openUrl } from "src/utils/urls";

export const SyncTemplates: FC = () => {
  const navigate = useNavigate();

  const { data: syncTemplatesData, isLoading: syncTemplatesLoading } = useSyncTemplatesQuery();

  const syncTemplates = syncTemplatesData?.sync_templates;

  const {
    data: { definitions: destinationDefinitions },
    loading: destinationsLoading,
  } = useDestinations();

  const { data: sources, loading: sourcesLoading } = useSources();

  if (destinationsLoading || !destinationDefinitions || sourcesLoading || syncTemplatesLoading) {
    return <PageSpinner />;
  }

  const columns = [
    {
      name: "Name",
      key: "name",
      cell: (name) => <Text sx={{ fontWeight: "semi" }}>{name}</Text>,
    },
    {
      name: "Parent model",
      cell: ({ segment: { name, connection } }) => {
        const source = sources.find((source) => source.id === connection.id);

        return (
          <Row sx={{ alignItems: "center" }}>
            <SourceIcon source={source} sx={{ width: "18px", mr: 2, flexShrink: 0 }} />
            <Text sx={{ fontWeight: "semi" }}>{name}</Text>
            <SourceBadges source={source} />
          </Row>
        );
      },
    },
    {
      name: "Destination",
      cell: ({ destination: { name, type } }) => {
        const definition = destinationDefinitions.find((def) => def.type === type);

        return (
          <Row sx={{ alignItems: "center" }}>
            <Image alt={definition?.name} src={definition?.icon} sx={{ width: "18px", mr: 2, flexShrink: 0 }} />
            <Text sx={{ fontWeight: "semi" }}>{name}</Text>
          </Row>
        );
      },
    },
    LastUpdatedColumn,
  ];

  return (
    <PermissionProvider permissions={[{ resource: "sync_template", grants: [ResourcePermissionGrant.Create] }]}>
      <Page crumbs={[{ label: "Audiences", link: "/audiences" }, { label: "Sync templates" }]} size="full">
        <Row
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            mb: 5,
            width: "100%",
          }}
        >
          <Menu
            options={[
              {
                label: "Parent models",
                description: "Models that audiences are built from",
                onClick: () => navigate("/audiences/setup/parent-models"),
              },
              {
                label: "Related models",
                description: "Models that can be joined with parent models",
                onClick: () => navigate("/audiences/setup/related-models"),
              },
              {
                label: "Events",
                description: "Models that filter audiences by timestamped events",
                onClick: () => navigate("/audiences/setup/events"),
              },
              {
                label: "Sync templates",
                description: "Configure syncs for audiences",
                onClick: () => navigate("/audiences/setup/sync-templates"),
              },
            ]}
          >
            <Row sx={{ alignItems: "center" }}>
              <Heading sx={{ mr: 2 }}>Sync templates</Heading>
              <ChevronDownIcon />
            </Row>
          </Menu>
          <Permission>
            <Button
              onClick={() => {
                navigate(`/audiences/setup/sync-templates/new`);
              }}
            >
              Add sync template
            </Button>
          </Permission>
        </Row>
        <Table
          columns={columns}
          data={syncTemplates}
          error={false}
          placeholder={{
            title: `No sync templates`,
            body: `Add a sync template to get started`,
            error: `Sync templates failed to load, please try again.`,
          }}
          onRowClick={({ id }, event) => openUrl(`/audiences/setup/sync-templates/${id}`, navigate, event)}
        />
      </Page>
    </PermissionProvider>
  );
};
